import { Link } from 'react-router-dom';
import { contentPrinciples, faqs, siteInfo, testimonials } from '@/mocks/site';
import Breadcrumb from '@/components/feature/Breadcrumb';
import FaqAccordion from '@/components/feature/FaqAccordion';
import Reveal from '@/components/base/Reveal';
import JsonLd from '@/components/base/JsonLd';
import { useSeo, buildAbsoluteUrl } from '@/hooks/useSeo';

const title = '关于我们 - 黑料大赛作品合集站的内容原则 | 黑料大赛';
const description =
  '了解黑料大赛作品合集站的定位与内容原则：来源可查、不作定论、拒绝越界、持续更正，以及我们的联系与合作方式。';
const keywords = '黑料大赛,关于我们,内容原则,黑料大赛作品合集,联系合作';

export default function About() {
  useSeo({ title, description, keywords, path: '/about' });

  const pageUrl = buildAbsoluteUrl('/about');
  const schema = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'AboutPage',
        name: title,
        url: pageUrl,
        description,
        inLanguage: 'zh-CN',
      },
      {
        '@type': 'Organization',
        name: siteInfo.name,
        url: buildAbsoluteUrl('/'),
        email: siteInfo.email,
        description: siteInfo.description,
        slogan: siteInfo.slogan,
      },
      {
        '@type': 'ContactPage',
        name: '联系黑料大赛',
        url: pageUrl,
        inLanguage: 'zh-CN',
        description: '投稿、更正与合作请通过页面邮箱联系我们。',
      },
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: '首页', item: buildAbsoluteUrl('/') },
          { '@type': 'ListItem', position: 2, name: '关于我们', item: pageUrl },
        ],
      },
      {
        '@type': 'FAQPage',
        mainEntity: faqs.map((item) => ({
          '@type': 'Question',
          name: item.q,
          acceptedAnswer: { '@type': 'Answer', text: item.a },
        })),
      },
    ],
  };

  return (
    <>
      <JsonLd data={schema} />

      <section className="relative w-full overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=Warm%20editorial%20abstract%20background%20of%20layered%20paper%20archives%20and%20index%20cards%2C%20deep%20crimson%20accents%20with%20antique%20gold%20light%2C%20soft%20directional%20light%20and%20long%20shadows%2C%20no%20text%2C%20no%20people%2C%20minimal%20magazine%20aesthetic%2C%20highly%20detailed&width=1600&height=700&seq=about-hero-01&orientation=landscape"
            alt="黑料大赛关于我们页面首图"
            title="黑料大赛 - 关于我们"
            className="h-full w-full object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/55 via-black/45 to-black/60"></div>
        </div>
        <div className="relative mx-auto w-full px-4 py-16 md:px-6 md:py-24">
          <div className="[&_a]:text-white/70 [&_span]:text-white/90">
            <Breadcrumb items={[{ label: '首页', to: '/' }, { label: '关于我们' }]} />
          </div>
          <h1 className="mt-4 max-w-3xl font-heading text-3xl font-black text-white md:text-4xl">
            关于黑料大赛作品合集站
          </h1>
          <p className="mt-5 max-w-2xl font-body text-sm leading-relaxed text-white/85 md:text-base">
            我们把散落在各个平台的赛事投稿聚拢起来，加一层编辑复核，再按主题与热度重新排好。
            目标很简单：让想围观的人看得省力，让愿意写的人有地方被看见。
          </p>
        </div>
      </section>

      <section className="w-full bg-background-50 py-14 md:py-20">
        <div className="mx-auto grid w-full grid-cols-1 gap-10 px-4 md:px-6 lg:grid-cols-3 lg:gap-12">
          <Reveal>
            <h2 className="font-heading text-xl font-bold text-foreground-950">网站定位</h2>
            <p className="mt-3 font-body text-sm leading-relaxed text-foreground-600">
              黑料大赛是一个内容聚合与围观平台，不是新闻机构，也不做即时爆料。我们更在意把一件事的来龙去脉讲清楚，
              而不是抢发布时间。作品以公开信息整理为主，涉及个人隐私的部分一律不收。
            </p>
          </Reveal>
          <Reveal delay={100}>
            <h2 className="font-heading text-xl font-bold text-foreground-950">发展理念</h2>
            <p className="mt-3 font-body text-sm leading-relaxed text-foreground-600">
              从第一季到现在，我们始终坚持一条：先把事实摆出来，再谈观点。也正因为如此，平台上的作品普遍节奏偏慢，
              但经得起回头再看。我们希望读者在这里养成自己判断的习惯，而不是被标题牵着走。
            </p>
          </Reveal>
          <Reveal delay={200}>
            <h2 className="font-heading text-xl font-bold text-foreground-950">我们服务谁</h2>
            <p className="mt-3 font-body text-sm leading-relaxed text-foreground-600">
              一是喜欢顺着时间线看完整事件的围观者，二是需要行业细节做参考的从业者，三是愿意花时间写长文的投稿人。
              这三类人构成了黑料大赛的内容生态，也决定了我们选择什么样的作品。
            </p>
          </Reveal>
        </div>
      </section>

      <section className="w-full border-t border-background-200 bg-background-100 py-14 md:py-20">
        <div className="mx-auto w-full px-4 md:px-6">
          <h2 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">四条内容原则</h2>
          <p className="mt-3 max-w-2xl font-body text-sm leading-relaxed text-foreground-600">
            这四条不是口号，而是每一篇作品在复核阶段都要过一遍的标准。
          </p>
          <div className="mt-10 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {contentPrinciples.map((item, index) => (
              <Reveal key={item.title} delay={index * 70}>
                <div className="flex h-full flex-col rounded-lg border border-background-200 bg-background-50 p-5">
                  <span className="flex h-11 w-11 items-center justify-center rounded-md bg-secondary-100 text-secondary-700">
                    <i className={`${item.icon} text-xl`}></i>
                  </span>
                  <h3 className="mt-4 font-heading text-base font-bold text-foreground-950">{item.title}</h3>
                  <p className="mt-2 font-body text-sm leading-relaxed text-foreground-600">{item.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="w-full bg-background-50 py-14 md:py-20">
        <div className="mx-auto w-full px-4 md:px-6">
          <h2 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">读者口碑</h2>
          <div className="mt-10 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {testimonials.map((item, index) => (
              <Reveal key={item.id} delay={index * 70}>
                <figure className="flex h-full flex-col rounded-lg border border-background-200 bg-background-100 p-5">
                  <blockquote className="flex-1 font-body text-sm leading-relaxed text-foreground-700">
                    {item.text}
                  </blockquote>
                  <figcaption className="mt-5 border-t border-background-200 pt-4">
                    <span className="block font-heading text-sm font-bold text-foreground-950">{item.name}</span>
                    <span className="mt-0.5 block font-label text-xs text-foreground-500">{item.role}</span>
                  </figcaption>
                </figure>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="w-full border-t border-background-200 bg-secondary-50 py-14 md:py-20">
        <div className="mx-auto grid w-full grid-cols-1 gap-10 px-4 md:px-6 lg:grid-cols-2 lg:gap-14">
          <Reveal>
            <span className="font-label text-xs tracking-[0.2em] text-secondary-700">联系我们</span>
            <h2 className="mt-3 font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
              投稿、更正与合作
            </h2>
            <p className="mt-4 max-w-xl font-body text-sm leading-relaxed text-foreground-600">
              投稿请附上作品主题、大致内容与信息来源说明；如果有作品需要更正，也可以直接发邮件，
              编辑会核对后更新，并保留更正记录。
            </p>
            <div className="mt-8 flex flex-col gap-4">
              <a
                href={`mailto:${siteInfo.email}`}
                className="flex items-center gap-3 rounded-lg border border-background-200 bg-background-50 p-4 transition-colors hover:border-primary-300"
              >
                <span className="flex h-10 w-10 items-center justify-center rounded-md bg-primary-100 text-primary-700">
                  <i className="ri-mail-line text-lg"></i>
                </span>
                <span>
                  <span className="block font-label text-xs text-foreground-500">投稿与一般咨询</span>
                  <span className="block font-body text-sm text-foreground-900">{siteInfo.email}</span>
                </span>
              </a>
              <a
                href={`mailto:${siteInfo.cooperation}`}
                className="flex items-center gap-3 rounded-lg border border-background-200 bg-background-50 p-4 transition-colors hover:border-primary-300"
              >
                <span className="flex h-10 w-10 items-center justify-center rounded-md bg-accent-100 text-accent-800">
                  <i className="ri-briefcase-line text-lg"></i>
                </span>
                <span>
                  <span className="block font-label text-xs text-foreground-500">内容合作</span>
                  <span className="block font-body text-sm text-foreground-900">{siteInfo.cooperation}</span>
                </span>
              </a>
              <div className="flex items-center gap-3 rounded-lg border border-background-200 bg-background-50 p-4">
                <span className="flex h-10 w-10 items-center justify-center rounded-md bg-secondary-100 text-secondary-700">
                  <i className="ri-time-line text-lg"></i>
                </span>
                <span>
                  <span className="block font-label text-xs text-foreground-500">回复时间</span>
                  <span className="block font-body text-sm text-foreground-900">工作日 10:00 - 18:00，一般当天回复</span>
                </span>
              </div>
            </div>
          </Reveal>

          <Reveal delay={120}>
            <h2 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">常见问题</h2>
            <p className="mt-3 font-body text-sm text-foreground-600">收录标准与榜单算法在这几条里说清楚了。</p>
            <div className="mt-6">
              <FaqAccordion items={faqs} />
            </div>
            <Link
              to="/works"
              className="mt-8 inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 font-label text-sm text-background-50 transition-colors hover:bg-primary-600"
            >
              <i className="ri-stack-line"></i>
              去看看作品合集
            </Link>
          </Reveal>
        </div>
      </section>
    </>
  );
}